/**
 * publishSphereType.ts
 *
 * The composer's publish call (EAINT-895 Phase 4). Cuts the caller's working
 * draft over to published via the schema-specific `POST /schemas/:id/publish`
 * — NOT the generic `POST /templates/:id/versions/:version/publish` the spec
 * names, which operates on a table `update-schema`'s `mode: 'draft'` path
 * never populates for a schema (see the backend route's own doc comment).
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { GateCheck } from './schemaApi';

export type PublishSphereTypeOutcome =
  | { outcome: 'published'; version: number }
  | { outcome: 'blocked'; checks: GateCheck[] };

function statusOf(err: unknown): number | undefined {
  const anyErr = err as { response?: { status?: number }; status?: number } | undefined;
  return anyErr?.response?.status ?? anyErr?.status;
}

function checksOf(err: unknown): GateCheck[] {
  const anyErr = err as { response?: { data?: { checks?: GateCheck[] } } } | undefined;
  return anyErr?.response?.data?.checks ?? [];
}

/**
 * A 400 (unresolved gate checks) resolves to `'blocked'` rather than
 * throwing — the client-side gate bar already disables Publish while checks
 * are open, so this is a defense-in-depth outcome, not an exceptional
 * failure. Every other error rethrows for the caller's generic error handling.
 */
export async function publishSphereType(schemaClient: ApiClient, schemaId: string): Promise<PublishSphereTypeOutcome> {
  try {
    const response = await schemaClient.post<{ published: boolean; version: number }>(
      `/schemas/${encodeURIComponent(schemaId)}/publish`,
      {}
    );
    return { outcome: 'published', version: response.version };
  } catch (err) {
    if (statusOf(err) === 400) {
      return { outcome: 'blocked', checks: checksOf(err) };
    }
    throw err;
  }
}
